import { useState, useCallback, useRef, useEffect } from "react";

interface UseDropdownClosingReturn {
  isDropdownClosing: boolean;
  onDropdownOpenChange: (open: boolean) => void;
  onDropdownSelect: (callback: () => void) => void;
}

/**
 * Custom hook to handle Radix UI dropdown closing animation
 * Prevents modal/other content from opening before dropdown animation completes
 *
 * `isDropdownClosing` is also tracked in a ref, since a plain state read
 * inside the `onDropdownSelect` timeout closes over the value from the
 * render that scheduled it — always the pre-`setIsDropdownClosing(true)`
 * value, a no-op guard rather than a real one. The ref has to be read
 * *before* `onDropdownSelect` sets it, not inside its own timeout: this
 * call always sets the ref itself, so a live read at fire time is always
 * `true` and the callback would never run. Capturing it upfront answers
 * the right question instead — was the menu already mid-close from a
 * separate, unrelated interaction when this selection happened.
 *
 * The default must clear the menu's actual exit animation
 * (`animate-fadeOut`, 200ms — see tailwind.config.js) with margin, which
 * the previous 150ms did not: the point of the delay is to let the menu
 * finish unmounting before the selected action opens a dialog on top of
 * it, and a delay shorter than the animation fires the action while the
 * menu is still on screen.
 *
 * The close-reset timer is tracked in a ref and cancelled on every reopen
 * and every new close, not just scheduled fresh each time: a second close
 * within `animationDuration` of a first one would otherwise leave the
 * first timer running on its original schedule, so it fires mid-second-
 * close and clears `isDropdownClosing` before that cycle's own exit
 * animation has actually finished. Selection timers are deliberately left
 * uncancelled on a later select: callers rely on them to open a dialog after
 * the dropdown closes, and a newer selection isn't grounds to drop an
 * older one's callback — only unmounting the hook owner clears them. They
 * are therefore held in a set rather than a single handle, so a second
 * select before unmount doesn't leave the first one uncancelled and free to
 * fire its callback into a torn-down tree.
 */
export const useDropdownClosing = (
  animationDuration: number = 250
): UseDropdownClosingReturn => {
  const [isDropdownClosing, setIsDropdownClosing] = useState(false);
  const isDropdownClosingRef = useRef(false);
  const closeResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const selectTimeoutsRef = useRef(new Set<ReturnType<typeof setTimeout>>());

  const setDropdownClosing = useCallback((value: boolean) => {
    isDropdownClosingRef.current = value;
    setIsDropdownClosing(value);
  }, []);

  const clearCloseResetTimeout = useCallback(() => {
    if (closeResetTimeoutRef.current === null) return;
    clearTimeout(closeResetTimeoutRef.current);
    closeResetTimeoutRef.current = null;
  }, []);

  const onDropdownOpenChange = useCallback(
    (open: boolean) => {
      clearCloseResetTimeout();

      if (open) {
        // A reopen before the previous close's timer elapsed would
        // otherwise leave the ref stuck `true`, making the next
        // onDropdownSelect on *this* open see a stale "already closing"
        // and silently drop the selected action.
        setDropdownClosing(false);
        return;
      }

      setDropdownClosing(true);
      closeResetTimeoutRef.current = setTimeout(() => {
        closeResetTimeoutRef.current = null;
        setDropdownClosing(false);
      }, animationDuration);
    },
    [animationDuration, clearCloseResetTimeout, setDropdownClosing]
  );

  const onDropdownSelect = useCallback(
    (callback: () => void) => {
      const wasAlreadyClosing = isDropdownClosingRef.current;
      setDropdownClosing(true);
      const handle = setTimeout(() => {
        selectTimeoutsRef.current.delete(handle);
        if (!wasAlreadyClosing) {
          callback();
        }
      }, animationDuration);
      selectTimeoutsRef.current.add(handle);
    },
    [animationDuration, setDropdownClosing]
  );

  useEffect(() => {
    // Read once here rather than in the cleanup: the set itself is created
    // with the ref and never reassigned, so this is the same object the
    // callbacks add to, and it keeps the ref-in-cleanup lint rule honest.
    const pending = selectTimeoutsRef.current;
    return () => {
      clearCloseResetTimeout();
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, [clearCloseResetTimeout]);

  return {
    isDropdownClosing,
    onDropdownOpenChange,
    onDropdownSelect
  };
};
